import { AxiosError } from 'axios';
import { isBusinessError } from './businessErrorHandler';

/**
 * Extracts error message from various error types, prioritizing backend response messages
 */
export const extractErrorMessage = (error: unknown): string => {
  // Handle AxiosError with response data
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;
    
    // Check if response data has a message field
    if (responseData && typeof responseData === 'object' && 'message' in responseData) {
      const message = responseData.message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    
    // Check if response data is a string
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }
    
    // Fallback to AxiosError message
    if (error.message) {
      return error.message;
    }
  }
  
  // Handle error objects with response property (non-AxiosError)
  if (error && typeof error === 'object' && 'response' in error) {
    const errorWithResponse = error as { response?: { data?: { message?: string } | string } };
    const responseData = errorWithResponse.response?.data;
    
    if (responseData) {
      if (typeof responseData === 'object' && 'message' in responseData) {
        const message = responseData.message;
        if (typeof message === 'string' && message.trim()) {
          return message;
        }
      } else if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
      }
    }
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Fallback for unknown error types
  return 'An unexpected error occurred. Please try again.';
};

type StandardizedAxiosError = Error & {
  isAxiosError: true;
  response: {
    status: number;
    statusText: string;
  };
};

/**
 * Creates a standardized error object with extracted message
 * Preserves AxiosError status information for retry and error handling logic
 */
export const createStandardizedError = (error: unknown): Error => {
  const message = extractErrorMessage(error);
  const standardizedError = new Error(message);
  
  // Preserve AxiosError status information for error handling logic
  if (error instanceof AxiosError && error.response?.status) {
    const errorWithStatus = standardizedError as StandardizedAxiosError;
    errorWithStatus.response = {
      status: error.response.status,
      statusText: error.response.statusText,
    };
    errorWithStatus.isAxiosError = true;
  }
  
  return standardizedError;
};

/**
 * Type guard to check if error has response data with message
 */
export const hasResponseMessage = (error: unknown): error is { response: { data: { message: string } } } => {
  return (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    error.response !== null &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    error.response.data !== null &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data &&
    typeof error.response.data.message === 'string'
  );
};

/**
 * Checks if an error is a 422 business logic error
 * These errors are handled by the business error dialog, so toasts should not be shown
 */
export const isBusinessLogicError = (error: unknown): boolean => {
  return isBusinessError(error);
};

/**
 * Shows an error toast, but skips showing toast for 422 errors
 * since those are already handled by the business error dialog
 * 
 * @param error - The error object
 * @param toast - The toast object from 'sonner' (e.g., `toast` from `import { toast } from 'sonner'`)
 * @param defaultMessage - Optional default message if error extraction fails
 */
export const showErrorToast = (
  error: unknown,
  toast: { error: (message: string) => void },
  defaultMessage?: string
): void => {
  // Don't show toast for 422 errors - they're handled by the dialog
  if (isBusinessLogicError(error)) {
    return;
  }
  
  const message = extractErrorMessage(error) || defaultMessage || 'An error occurred';
  toast.error(message);
}; 